function required(value,label,maxLength){
  const text=String(value||'').trim();
  if(!text)throw new TypeError(`${label} is required`);
  if(text.length>maxLength)throw new TypeError(`${label} is too long`);
  return text;
}

function optionalText(value,maxLength){
  if(value==null||value==='')return null;
  const text=String(value).trim();
  if(text.length>maxLength)throw new TypeError('Text value is too long');
  return text||null;
}

function normaliseSmokerTypeKey(value){
  if(value==null||value==='')return null;
  const key=String(value).trim().toLowerCase();
  if(!/^[a-z][a-z0-9_]{1,63}$/.test(key))throw new TypeError('Invalid smokerTypeKey');
  return key;
}

function idOrGenerated(value){
  const id=String(value||'').trim();
  if(id)return id;
  const generated=globalThis.crypto?.randomUUID?.();
  if(generated)return generated;
  throw new TypeError('id is required when crypto.randomUUID is unavailable');
}

function normaliseSortOrder(value){
  const order=value==null||value===''?0:Number(value);
  if(!Number.isInteger(order)||order<0||order>1000000)throw new TypeError('sortOrder must be an integer between 0 and 1000000');
  return order;
}

export function createSmoker({
  id,
  userId,
  name,
  smokerTypeKey=null,
  notes=null,
  archivedAt=null,
  sortOrder=0,
  isDefault=false,
  photoPath=null
}={}){
  return Object.freeze({
    id:idOrGenerated(id),
    userId:required(userId,'userId',128),
    name:required(name,'name',100),
    smokerTypeKey:normaliseSmokerTypeKey(smokerTypeKey),
    notes:optionalText(notes,2000),
    archivedAt:archivedAt?String(archivedAt):null,
    sortOrder:normaliseSortOrder(sortOrder),
    isDefault:Boolean(isDefault),
    photoPath:optionalText(photoPath,512)
  });
}

export function updateSmoker(smoker,changes={}){
  const current=createSmoker(smoker);
  return createSmoker({
    ...current,
    ...changes,
    id:current.id,
    userId:current.userId
  });
}

export function renameSmoker(smoker,name){
  return updateSmoker(smoker,{name});
}

export function archiveSmoker(smoker,{now=new Date().toISOString()}={}){
  return updateSmoker(smoker,{archivedAt:now,isDefault:false});
}

export function restoreSmoker(smoker){
  return updateSmoker(smoker,{archivedAt:null});
}

export function orderSmokers(smokers,{includeArchived=false}={}){
  if(!Array.isArray(smokers))throw new TypeError('smokers must be an array');
  return Object.freeze(
    smokers
      .map(createSmoker)
      .filter(smoker=>includeArchived||!smoker.archivedAt)
      .sort((left,right)=>
        left.sortOrder-right.sortOrder
        ||String(left.id).localeCompare(String(right.id))
      )
  );
}

export function effectiveDefaultSmoker(smokers){
  const active=orderSmokers(smokers);
  if(!active.length)return null;
  return active.find(smoker=>smoker.isDefault)||active[0];
}

export function setDefaultSmoker(smokers,targetId){
  const target=String(targetId||'').trim();
  const active=orderSmokers(smokers,{includeArchived:true});
  const selected=active.find(smoker=>smoker.id===target);
  if(!selected||selected.archivedAt)throw new TypeError('Active default smoker is required');

  return Object.freeze(active.map(smoker=>updateSmoker(smoker,{
    isDefault:!smoker.archivedAt&&smoker.id===target
  })));
}

export function planSmokerDeletion(smokers,targetId,{historicalSessionCount=0}={}){
  const all=orderSmokers(smokers,{includeArchived:true});
  const target=all.find(smoker=>smoker.id===String(targetId||'').trim());
  if(!target||target.archivedAt)throw new TypeError('Active smoker is required');

  const history=Number(historicalSessionCount);
  if(!Number.isInteger(history)||history<0)throw new TypeError('historicalSessionCount must be a non-negative integer');

  const defaultBefore=effectiveDefaultSmoker(all);
  const remaining=all.filter(smoker=>!smoker.archivedAt&&smoker.id!==target.id);
  const replacement=remaining.length?effectiveDefaultSmoker(remaining):null;

  return Object.freeze({
    smokerId:target.id,
    action:'archive',
    historicalSessionCount:history,
    hasHistoricalSessions:history>0,
    wasEffectiveDefault:defaultBefore?.id===target.id,
    replacementDefaultId:defaultBefore?.id===target.id?replacement?.id||null:defaultBefore?.id||null,
    requiresReplacementSelection:defaultBefore?.id===target.id&&remaining.length>1,
    leavesNoActiveSmoker:remaining.length===0
  });
}

export function applySmokerDeletionPlan(smokers,plan,{now=new Date().toISOString(),replacementDefaultId=plan?.replacementDefaultId}={}){
  if(!plan?.smokerId||plan.action!=='archive')throw new TypeError('Valid smoker deletion plan is required');
  let next=orderSmokers(smokers,{includeArchived:true}).map(smoker=>
    smoker.id===plan.smokerId?archiveSmoker(smoker,{now}):smoker
  );

  const active=next.filter(smoker=>!smoker.archivedAt);
  if(!active.length)return Object.freeze(next);

  const requested=String(replacementDefaultId||'').trim();
  if(requested){
    next=[...setDefaultSmoker(next,requested)];
  }else if(!effectiveDefaultSmoker(next)?.isDefault){
    next=[...setDefaultSmoker(next,effectiveDefaultSmoker(next).id)];
  }
  return Object.freeze(next);
}

export function smokerToRow(smoker){
  const value=createSmoker(smoker);
  return Object.freeze({
    id:value.id,
    user_id:value.userId,
    name:value.name,
    smoker_type_key:value.smokerTypeKey,
    notes:value.notes,
    archived_at:value.archivedAt,
    sort_order:value.sortOrder,
    is_default:value.isDefault,
    photo_path:value.photoPath
  });
}

export function smokerFromRow(row={}){
  return createSmoker({
    id:row.id,
    userId:row.user_id,
    name:row.name,
    smokerTypeKey:row.smoker_type_key,
    notes:row.notes,
    archivedAt:row.archived_at,
    sortOrder:row.sort_order,
    isDefault:row.is_default,
    photoPath:row.photo_path
  });
}
